import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  ParsedQuote,
  PlatformId,
  QuoteLineItem,
  QuoteLinePreview,
  WorkOrderSummary,
  QuoteSubmitResult,
} from '@tcg/shared';
import { api, ApiClientError, documentsApi } from '../lib/api';
import { useSession } from '../lib/session';
import { formatMoney } from '../lib/format';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, PageHeader, Select, Spinner } from '../components/ui';
import { IconQuote } from '../components/icons';
import { WorkOrderPicker } from '../components/WorkOrderPicker';

// Quotes are always submitted in USD; there is no currency picker.
const QUOTE_CURRENCY = 'USD';

/** What Corrigo will price the resolved lines at: sum of quantity × rate (cents). */
function pricedLineSubtotal(preview: QuoteLinePreview): number {
  return preview.resolved.reduce(
    (sum, r) => sum + (r.unmappedReason ? 0 : Math.round((r.quantity ?? 0) * (r.rate ?? 0) * 100) / 100),
    0,
  );
}

/** True when the priced lines don't reconcile to the proposal's pre-tax subtotal. */
function previewAmountOff(preview: QuoteLinePreview | null, expectedSubtotal: number | null): boolean {
  // Reconciliation only applies when there are resolved, priced lines (Corrigo's
  // SKU mapping). Platforms without price-list resolution (ServiceChannel) return
  // an empty `resolved` list, so there is nothing to reconcile — never gate them
  // (a 0 priced subtotal would otherwise always look "off" vs the proposal total).
  if (!preview || !preview.allResolved || preview.resolved.length === 0 || expectedSubtotal == null) {
    return false;
  }
  return Math.abs(pricedLineSubtotal(preview) - expectedSubtotal) > Math.max(0.01, 0.01 * preview.resolved.length);
}

interface Draft {
  woNumber: string;
  proposalNumber: string;
  amount: string;
  currency: string;
  comment: string;
  lineItems: QuoteLineItem[];
}

const emptyDraft: Draft = {
  woNumber: '',
  proposalNumber: '',
  amount: '',
  currency: QUOTE_CURRENCY,
  comment: '',
  lineItems: [],
};

export function SubmitQuote() {
  const { connections } = useSession();
  const ready = useMemo(() => connections.filter((c) => c.canSubmit), [connections]);

  const [platform, setPlatform] = useState<PlatformId | ''>(ready[0]?.platform ?? '');
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [parsed, setParsed] = useState<ParsedQuote | null>(null);
  // The original uploaded proposal PDF, retained so it can optionally be attached
  // to the work order at submit time ("Include PDF Copy"). Null when the user
  // typed the fields in manually instead of uploading a PDF.
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [includePdf, setIncludePdf] = useState(false);
  // Outcome of the optional PDF attach, shown on the success screen.
  const [pdfAttached, setPdfAttached] = useState(false);
  const [pdfAttachError, setPdfAttachError] = useState<string | null>(null);
  // The reconciliation/row gates block submit until the user explicitly confirms.
  const [gateOverride, setGateOverride] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  // The work-order number we've committed to resolving (set when the user looks it
  // up — on Enter, on field blur, via the Look up button, or after a PDF parse).
  // Drives the lazy lookup below: the work order and line mapping populate as soon
  // as this is non-empty, with no separate "Next" step.
  const [committedWo, setCommittedWo] = useState('');
  // Bumped on every explicit lookup so the Corrigo picker re-searches even when the
  // number is unchanged (e.g. retrying after a transient "not found").
  const [lookupNonce, setLookupNonce] = useState(0);
  const [workOrder, setWorkOrder] = useState<WorkOrderSummary | null>(null);
  // number→id picker: which WorkOrderId the user resolved to (Corrigo can match >1).
  const [selectedWoId, setSelectedWoId] = useState<number | null>(null);
  const [preview, setPreview] = useState<QuoteLinePreview | null>(null);
  const [result, setResult] = useState<QuoteSubmitResult | null>(null);
  const [busy, setBusy] = useState(false); // parse / submit (gates the Submit button)
  const [resolving, setResolving] = useState(false); // work-order lookup + first preview
  const [previewLoading, setPreviewLoading] = useState(false); // tax-triggered re-resolve
  const [error, setError] = useState<string | null>(null);

  // Review mode (Corrigo): final submission to the customer is held by an admin.
  // The whole flow still runs — only the last "Submit quote" action is disabled.
  const activeConn = useMemo(
    () => connections.find((c) => c.platform === platform) ?? null,
    [connections, platform],
  );
  const finalSubmitHeld = activeConn?.allowFinalSubmit === false;

  // Resolve parsed line items against the customer's price-list mapping (Corrigo).
  const committedRef = useRef('');

  // Forget any resolved work order / mapping. Called when the number, platform, or
  // the uploaded proposal changes so a stale work order is never shown or submitted.
  const clearResolution = () => {
    setCommittedWo('');
    setWorkOrder(null);
    setSelectedWoId(null);
    setPreview(null);
    committedRef.current = '';
  };

  // Commit a typed work-order number and kick off the lookup. Reads the value from
  // the field event (not the draft closure) so a fast type-then-blur never looks up
  // a stale value, and bumps the nonce so an unchanged number still re-searches.
  const commitWo = (value: string) => {
    const v = value.trim();
    if (!v) return;
    setWorkOrder(null);
    setSelectedWoId(null);
    setPreview(null);
    committedRef.current = ''; // let the direct-lookup effect (non-Corrigo) re-run
    setLookupNonce((n) => n + 1);
    setCommittedWo(v);
  };

  if (ready.length === 0) {
    return (
      <Banner tone="warn">
        No platform is ready to accept quotes yet.{' '}
        <Link to="/" className="font-medium underline">
          Go to the dashboard
        </Link>{' '}
        to connect Corrigo or ServiceChannel.
      </Banner>
    );
  }

  const applyParsed = (p: ParsedQuote) => {
    setDraft({
      woNumber: p.woNumber ?? '',
      proposalNumber: p.proposalNumber ?? '',
      amount: p.amount != null ? String(p.amount) : '',
      // Always USD regardless of what the proposal PDF prints.
      currency: QUOTE_CURRENCY,
      comment: p.comment ?? '',
      lineItems: p.lineItems,
    });
    setParsed(p);
    setGateOverride(false);
    setWarnings(p.warnings);
    // Kick off the lazy work-order lookup straight away when the parse found a
    // number — the work order and line mapping then populate on their own.
    setWorkOrder(null);
    setSelectedWoId(null);
    setPreview(null);
    committedRef.current = '';
    setLookupNonce((n) => n + 1);
    setCommittedWo(p.woNumber?.trim() ?? '');
  };

  // Submit is gated until the parse reconciles (or the user explicitly confirms).
  const submitGated = parsed != null && parsed.blocking && !gateOverride;

  // Inline edit of a resolved line's quantity. Updates BOTH the draft (what is
  // submitted — Corrigo prices the line as quantity × rate) and the preview
  // snapshot (so the line total, the priced subtotal, and the reconcile gate all
  // recompute live). Indices align: the preview was built from draft.lineItems.
  const editLineQuantity = (index: number, quantity: number) => {
    const q = Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
    setDraft((d) => ({
      ...d,
      lineItems: d.lineItems.map((li, j) => (j === index ? { ...li, quantity: q } : li)),
    }));
    setPreview((p) =>
      p ? { ...p, resolved: p.resolved.map((r, j) => (j === index ? { ...r, quantity: q } : r)) } : p,
    );
  };

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      applyParsed(await api.parsePdf(file));
      // Keep the parsed proposal PDF so it can be attached at submit time.
      setPdfFile(file);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not parse the PDF');
    } finally {
      setBusy(false);
    }
  };

  // Fetch the (optionally picker-resolved) work order AND resolve the parsed line
  // items against the price-list mapping in one go, so the page shows the work
  // order, any mapping gaps, and the submit button together.
  const resolveAndPreview = async (workOrderId?: number) => {
    if (!platform || !committedWo) return;
    setResolving(true);
    setError(null);
    try {
      const wo = await api.getWorkOrder(platform, committedWo, workOrderId);
      setWorkOrder(wo);
      setSelectedWoId(workOrderId ?? null);
      // The mapping preview only matters when the WO can actually accept a quote.
      if (wo.canSubmitQuote) {
        setPreview(await fetchPreview(workOrderId ?? undefined, draft.lineItems));
      } else {
        setPreview(null);
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load the work order');
    } finally {
      setResolving(false);
    }
  };

  // Resolve parsed line items against the customer's price-list mapping (Corrigo).
  const fetchPreview = (workOrderId: number | undefined, lineItems: QuoteLineItem[]) =>
    api.previewLines(
      platform as PlatformId,
      committedWo,
      lineItems.map((li) => ({
        description: li.description,
        quantity: li.quantity,
        isTaxable: li.isTaxable,
        unitPrice: li.unitPrice,
        descriptionDetail: li.descriptionDetail,
      })),
      workOrderId,
    );

  // Re-resolve the mapping after a change that can move a line to a different
  // price-list item (e.g. toggling Taxable picks the Taxable vs Not-Taxable
  // variant). Quantity edits don't need this — they only rescale an existing rate.
  const refreshPreview = async (lineItems: QuoteLineItem[]) => {
    if (platform !== 'corrigo' || !workOrder?.canSubmitQuote) return;
    setPreviewLoading(true);
    setError(null);
    try {
      setPreview(await fetchPreview(selectedWoId ?? undefined, lineItems));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not re-check the line items');
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleLineTax = (index: number, value: boolean) => {
    const next = draft.lineItems.map((li, j) =>
      j === index ? { ...li, isTaxable: value, taxUncertain: false } : li,
    );
    setDraft((d) => ({ ...d, lineItems: next }));
    void refreshPreview(next);
  };

  // Lazy lookup for platforms with no number→id picker (ServiceChannel): once a
  // number is committed, resolve it directly. Corrigo goes through WorkOrderPicker
  // (rendered below), which calls resolveAndPreview with the chosen WorkOrderId.
  useEffect(() => {
    if (!committedWo || !platform || platform === 'corrigo') return;
    if (committedRef.current === `${platform}:${committedWo}`) return;
    committedRef.current = `${platform}:${committedWo}`;
    void resolveAndPreview();
    // resolveAndPreview reads current draft/platform; re-run only when the
    // committed number or platform changes (guarded by committedRef above).
  }, [committedWo, platform]);

  // Reset everything back to a blank upload so the user can go straight into the
  // next quote from the confirmation screen. The selected platform is intentionally
  // kept — quotes are usually entered in batches on one platform.
  const startNextQuote = () => {
    setDraft(emptyDraft);
    setParsed(null);
    setPdfFile(null);
    setIncludePdf(false);
    setPdfAttached(false);
    setPdfAttachError(null);
    setGateOverride(false);
    setWarnings([]);
    clearResolution();
    setResult(null);
    setError(null);
  };

  const submit = async () => {
    if (!platform) return;
    const amount = Number(draft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a valid amount greater than 0.');
      return;
    }
    setBusy(true);
    setError(null);
    setPdfAttached(false);
    setPdfAttachError(null);
    try {
      const res = await api.submitQuote({
        platform,
        woNumber: draft.woNumber.trim(),
        workOrderId: selectedWoId ?? undefined,
        amount,
        currency: draft.currency,
        comment: draft.comment,
        // Proposal tax → a Category='Tax' Corrigo quote line (Corrigo has no
        // quote-level tax field). `amount` stays the tax-inclusive total.
        tax: parsed?.totals.tax ?? undefined,
        lineItems: draft.lineItems.length ? draft.lineItems : undefined,
      });
      setResult(res);
      // Optionally attach the uploaded proposal PDF to the work order, reusing the
      // existing document-upload API. A failure here doesn't undo the quote — the
      // quote is already submitted — so it's surfaced as a non-blocking warning.
      if (includePdf && pdfFile && platform === 'corrigo') {
        try {
          await documentsApi.uploadGeneral(draft.woNumber.trim(), pdfFile, {
            workOrderId: selectedWoId ?? undefined,
            documentType: 'Document',
          });
          setPdfAttached(true);
        } catch (err) {
          setPdfAttachError(
            err instanceof ApiClientError ? err.message : 'Could not attach the PDF copy',
          );
        }
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  // Confirmation screen, shown after a successful submit.
  if (result) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader
          backTo="/"
          icon={<IconQuote className="h-5 w-5" />}
          title="Submit Quote"
          subtitle="Quote submitted."
        />
        <Card>
          <CardHeader title="Quote submitted" />
          <CardBody className="space-y-4">
            <Banner tone="success">
              Quote submitted — status <strong>{result.quoteStatus}</strong>.
            </Banner>
            {pdfAttached && (
              <Banner tone="success">A copy of the proposal PDF was attached to the work order.</Banner>
            )}
            {pdfAttachError && (
              <Banner tone="warn">
                The quote was submitted, but attaching the PDF copy failed: {pdfAttachError}.
              </Banner>
            )}
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Detail label="Work order" value={result.woNumber} />
              <Detail label="Amount" value={formatMoney(result.amount, result.currency)} />
              <Detail label="Submitted" value={new Date(result.submittedAt).toLocaleString()} />
              <Detail label="Receipt #" value={result.auditId} />
            </dl>
            <div className="flex items-center justify-between gap-4">
              <Link to="/" className="text-sm font-medium text-accent-600 hover:underline">
                Back to dashboard
              </Link>
              <Button onClick={startNextQuote}>Submit another quote</Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const showMapping = platform === 'corrigo';
  // Corrigo resolves a number→id with a picker; show it once a number is committed
  // and we don't yet have a work order. It auto-selects a single match.
  const showPicker = showMapping && committedWo !== '' && !workOrder && !resolving;
  // Whether the work-order section has anything to show yet.
  const showWorkOrderSection = showPicker || resolving || workOrder != null;
  const expectedSubtotal = parsed?.reconciliation.computedSubtotal ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        backTo="/"
        icon={<IconQuote className="h-5 w-5" />}
        title="Submit Quote"
        subtitle="Upload a quote PDF; the work order and line items verify and submit on one screen."
        actions={
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Platform</span>
            <Select
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value as PlatformId);
                clearResolution();
              }}
              className="w-auto"
            >
              {ready.map((c) => (
                <option key={c.platform} value={c.platform}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {error && <Banner tone="error">{error}</Banner>}

      {/* 1 — Quote details: drop a PDF to auto-fill, or type the fields in. */}
      <Card>
        <CardHeader
          title="Quote details"
          subtitle="Drop a proposal PDF to auto-fill, or enter the fields by hand. Nothing is submitted yet."
        />
        <CardBody className="space-y-4">
          <PdfDropArea busy={busy} onFile={(f) => void onFile(f)} />

          {warnings.length > 0 && (
            <Banner tone="warn">
              <ul className="list-disc pl-4">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Banner>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field
                label="Work order number (Client PO #)"
                hint="Press Enter or tab out to look it up."
              >
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    value={draft.woNumber}
                    onChange={(e) => {
                      setDraft({ ...draft, woNumber: e.target.value });
                      // Editing the number invalidates anything we resolved for the old one.
                      clearResolution();
                    }}
                    onBlur={(e) => commitWo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitWo(e.currentTarget.value);
                      }
                    }}
                    placeholder="WO-100245"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => commitWo(draft.woNumber)}
                    disabled={!draft.woNumber.trim() || resolving}
                  >
                    {resolving ? <Spinner /> : 'Look up'}
                  </Button>
                </div>
              </Field>
            </div>
            <Field label="Quoted amount" hint="Submitted in USD.">
              <Input
                type="number"
                step="0.01"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="1500.00"
              />
            </Field>
            <Field label="Proposal # (internal — not sent)">
              <Input
                value={draft.proposalNumber}
                onChange={(e) => setDraft({ ...draft, proposalNumber: e.target.value })}
                placeholder="11111032"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Comment">
                <Input
                  value={draft.comment}
                  onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                  placeholder="Quote for additional parts and labor"
                />
              </Field>
            </div>
          </div>

          {parsed && <Reconciliation parsed={parsed} currency={draft.currency} />}
        </CardBody>
      </Card>

      {/* 2 — Work order: lazy resolution. Corrigo disambiguates a number→id with the
          picker; other platforms resolve directly via the effect above. */}
      {showWorkOrderSection && (
        <div className="space-y-4">
          {showPicker && (
            <WorkOrderPicker
              key={`${committedWo}#${lookupNonce}`}
              woNumber={committedWo}
              search={() => api.searchWorkOrders('corrigo', committedWo)}
              onResolved={(workOrderId) => void resolveAndPreview(workOrderId)}
              onReset={() => clearResolution()}
            />
          )}

          {resolving && (
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-500">
              <Spinner /> Loading the work order…
            </div>
          )}

          {workOrder && (
            <Card>
              <CardHeader
                title="Work order"
                action={
                  workOrder.canSubmitQuote ? (
                    <Badge tone="green">Can submit</Badge>
                  ) : (
                    <Badge tone="red">Blocked</Badge>
                  )
                }
              />
              <CardBody className="space-y-4">
                {!workOrder.canSubmitQuote && (
                  <Banner tone="error">{workOrder.reason ?? 'This work order cannot accept a quote.'}</Banner>
                )}
                {finalSubmitHeld && (
                  <Banner tone="info">
                    <strong>Review mode is on.</strong> Line items and the quote are prepared for review,
                    but the final submit to the customer is held. Verify the resolved Corrigo line items
                    below, then have an admin enable submission
                    (<code>CORRIGO_ALLOW_FINAL_SUBMIT=true</code>) to send it.
                  </Banner>
                )}
                <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                  <Detail label="Work order" value={workOrder.woNumber} />
                  <Detail label="Status" value={workOrder.status} />
                  <Detail label="Current NTE" value={formatMoney(workOrder.nteAmount, workOrder.currency)} />
                  <Detail label="Client" value={workOrder.client || '—'} />
                  <Detail label="Location" value={workOrder.location || '—'} />
                  <Detail label="Currency" value={workOrder.currency} />
                </dl>
                {workOrder.existingQuote && (
                  <Banner tone="info">
                    Existing quote: {formatMoney(workOrder.existingQuote.amount, workOrder.currency)} (
                    {workOrder.existingQuote.status})
                  </Banner>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* 3 — Line items: parsed extraction + (for Corrigo) the price-list mapping. */}
      {draft.lineItems.length > 0 && (
        <Card>
          <CardHeader
            title="Line items"
            subtitle={
              showMapping
                ? 'Parsed lines and their Corrigo price-list mapping. The mapping fills in once the work order resolves.'
                : 'Parsed from the proposal.'
            }
          />
          <CardBody>
            <Dimmable active={showMapping && (resolving || previewLoading)}>
              <MergedLineTable
                items={draft.lineItems}
                preview={preview}
                showMapping={showMapping}
                previewLoading={previewLoading}
                workOrderResolved={workOrder != null}
                currency={draft.currency}
                expectedSubtotal={expectedSubtotal}
                onToggleTax={toggleLineTax}
                onEditQuantity={editLineQuantity}
              />
            </Dimmable>
          </CardBody>
        </Card>
      )}

      {/* 4 — Submit: the gate, the optional PDF attach, and the action. */}
      <Card>
        <CardBody className="space-y-3">
          {submitGated && (
            <Banner tone="error">
              <div className="space-y-1">
                <p className="font-medium">
                  This proposal didn’t reconcile, or a row failed validation. Submit is blocked.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={gateOverride}
                    onChange={(e) => setGateOverride(e.target.checked)}
                  />
                  I’ve reviewed the discrepancies above and want to submit anyway.
                </label>
              </div>
            </Banner>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-slate-400">
              {workOrder
                ? 'Verify the work order and line items, then submit.'
                : 'Enter a work order number above to look it up.'}
            </span>
            <div className="flex items-center gap-4">
              {platform === 'corrigo' && (
                <label
                  className={`flex items-center gap-2 text-sm ${pdfFile ? 'text-slate-600' : 'text-slate-400'}`}
                  title={
                    pdfFile
                      ? 'Attach a copy of the uploaded proposal PDF to this work order.'
                      : 'Upload a quote PDF to include a copy.'
                  }
                >
                  <input
                    type="checkbox"
                    checked={includePdf}
                    disabled={busy || !pdfFile}
                    onChange={(e) => setIncludePdf(e.target.checked)}
                  />
                  Include PDF Copy
                </label>
              )}
              <Button
                onClick={submit}
                disabled={
                  busy ||
                  resolving ||
                  previewLoading ||
                  finalSubmitHeld ||
                  submitGated ||
                  !workOrder ||
                  !workOrder.canSubmitQuote ||
                  (preview != null && !preview.allResolved) ||
                  previewAmountOff(preview, expectedSubtotal)
                }
                title={
                  finalSubmitHeld
                    ? 'Final submission is disabled (review mode). An admin must enable CORRIGO_ALLOW_FINAL_SUBMIT.'
                    : undefined
                }
              >
                {busy ? <Spinner /> : finalSubmitHeld ? 'Submit held (review mode)' : 'Submit quote'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** Drag-and-drop (or click-to-browse) for the single quote PDF. */
function PdfDropArea({ busy, onFile }: { busy: boolean; onFile: (file: File) => void }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!busy) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (busy) return;
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 text-center text-sm transition ${
        over ? 'border-accent-500 bg-accent-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'
      } ${busy ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {busy ? (
        <Spinner />
      ) : (
        <>
          <span className="font-medium text-slate-600">Drop the quote PDF here or click to browse</span>
          <span className="mt-1 text-xs text-slate-400">PDF only · we extract editable fields, nothing is submitted yet</span>
        </>
      )}
    </div>
  );
}

/** Prominent green/red reconciliation result (parsed totals vs printed PDF). */
function Reconciliation({ parsed, currency }: { parsed: ParsedQuote; currency: string }) {
  const r = parsed.reconciliation;
  const rows = [
    { label: 'Line items sub total', parsed: r.computedSubtotal, printed: r.printedSubtotal },
    { label: 'Tax', parsed: r.printedTax ?? 0, printed: r.printedTax },
    { label: 'Total', parsed: r.computedTotal, printed: r.printedTotal },
  ];
  return (
    <div
      className={`rounded-lg border p-3 ${
        r.ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <Badge tone={r.ok ? 'green' : 'red'}>{r.ok ? 'Totals reconcile' : 'Does not reconcile'}</Badge>
        <span className="text-sm text-slate-600">
          {r.ok
            ? 'Parsed line items match the printed proposal.'
            : 'Parsed line items disagree with the printed proposal.'}
        </span>
      </div>
      <table className="mt-2 w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-400">
          <tr>
            <th className="py-1">Check</th>
            <th className="py-1 text-right">Parsed</th>
            <th className="py-1 text-right">Printed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const mismatch =
              row.printed != null && Math.abs((row.parsed ?? 0) - row.printed) > 0.01;
            return (
              <tr key={row.label} className="border-t border-black/5">
                <td className="py-1">{row.label}</td>
                <td className="py-1 text-right">{formatMoney(row.parsed, currency)}</td>
                <td className={`py-1 text-right ${mismatch ? 'font-semibold text-red-600' : ''}`}>
                  {row.printed != null ? formatMoney(row.printed, currency) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {r.discrepancies.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
          {r.discrepancies.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 font-medium text-slate-800">{value}</dd>
    </div>
  );
}

/**
 * Dim and lock a single section while it reloads in place, with a small "Updating…"
 * pill so the wait is visible without freezing the rest of the page.
 */
function Dimmable({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div className="relative">
      <div
        aria-busy={active}
        className={`transition-opacity duration-200 ${active ? 'pointer-events-none opacity-50' : ''}`}
      >
        {children}
      </div>
      {active && (
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-500 shadow-sm ring-1 ring-slate-200">
            <Spinner /> Updating…
          </span>
        </div>
      )}
    </div>
  );
}

const UNMAPPED_LABEL: Record<string, string> = {
  no_internal_item: 'no matching internal item',
  no_taxable_variant: 'needed Taxable/Not-Taxable variant missing',
  no_mapping: 'not mapped for this customer',
  option_inactive: 'mapped item inactive in Corrigo',
};

/**
 * One grid for the whole line-item review: the parsed columns (source, quantity,
 * Man Hr, unit, total, taxable) plus — for Corrigo — the price-list mapping and
 * the priced line total. The mapping columns populate lazily once the work order
 * is resolved; until then they show a muted placeholder.
 */
function MergedLineTable({
  items,
  preview,
  showMapping,
  previewLoading,
  workOrderResolved,
  currency,
  expectedSubtotal,
  onToggleTax,
  onEditQuantity,
}: {
  items: QuoteLineItem[];
  preview: QuoteLinePreview | null;
  showMapping: boolean;
  previewLoading: boolean;
  workOrderResolved: boolean;
  currency: string;
  expectedSubtotal: number | null;
  onToggleTax: (index: number, value: boolean) => void;
  onEditQuantity: (index: number, quantity: number) => void;
}) {
  const resolved = preview?.resolved ?? [];
  // What Corrigo will price these lines at (quantity × rate), compared against the
  // proposal's pre-tax subtotal so a rate that differs from the proposal (override
  // or rate-locked item) is visible BEFORE submit — the server blocks it anyway.
  const pricedSubtotal = preview ? pricedLineSubtotal(preview) : 0;
  const amountOff = previewAmountOff(preview, expectedSubtotal);
  const anyDetail = items.some((li) => li.descriptionDetail);

  return (
    <div className="space-y-2">
      {showMapping && preview && !preview.allResolved && (
        <Banner tone="warn">
          Some line items aren’t mapped to a Corrigo price-list item for this customer. An admin can
          fix this in <Link to="/admin/price-list" className="font-medium underline">Price List Mapping</Link>,
          then re-check. Submit is blocked until every line resolves.
        </Banner>
      )}
      {showMapping && amountOff && (
        <Banner tone="error">
          The mapped line items price out to {formatMoney(pricedSubtotal, currency)}, but the proposal’s
          line total is {formatMoney(expectedSubtotal, currency)}. Adjust a quantity below until the totals
          agree (a mapped rate may differ from the proposal). Submit is blocked until they match.
        </Banner>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
            <tr>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-center">Taxable</th>
              {showMapping && (
                <>
                  <th className="px-3 py-2">Corrigo mapping</th>
                  <th className="px-3 py-2 text-right">Line total</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((li, i) => {
              const r = resolved[i];
              return (
                <tr
                  key={i}
                  className={`border-t border-slate-100 align-top ${li.totalMismatch ? 'bg-red-50' : ''}`}
                >
                  <td className="px-3 py-2">
                    {li.section ? (
                      <Badge tone={li.section === 'incurred' ? 'slate' : 'amber'}>{li.section}</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {li.description}
                    {li.descriptionDetail && (
                      <span
                        className="mt-0.5 block text-xs italic text-slate-500"
                        title="Submitted as this line's Corrigo Description"
                      >
                        {li.descriptionDetail}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={li.quantity}
                        onChange={(e) => onEditQuantity(i, Number(e.target.value))}
                        className="w-20 rounded border border-slate-300 px-2 py-1 text-right tabular-nums focus:border-accent-500 focus:outline-none"
                        aria-label={`Quantity for ${li.description}`}
                      />
                      {li.totalMismatch && (
                        <span className="text-xs font-semibold text-red-600" title="Quantity × unit ≠ total">
                          ⚠
                        </span>
                      )}
                      {li.quantityDerived && (
                        <span
                          className="text-xs font-semibold text-amber-600"
                          title="No Man Hr/quantity was printed — inferred from total ÷ unit price. Confirm."
                        >
                          ≈
                        </span>
                      )}
                    </div>
                    {li.manHr != null && (
                      <span
                        className="mt-0.5 block text-xs text-slate-400"
                        title="Billable quantity = Qty × Man Hr from the proposal"
                      >
                        {li.qtyColumn ?? 1} × {li.manHr} hr
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{formatMoney(li.unitPrice, currency)}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(li.amount, currency)}</td>
                  <td className="px-3 py-2 text-center">
                    <label
                      className={`inline-flex items-center gap-1 ${
                        li.taxUncertain ? 'rounded bg-amber-100 px-1' : ''
                      }`}
                      title={li.taxUncertain ? 'Tax checkbox was unreadable — confirm it' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={li.isTaxable ?? false}
                        onChange={(e) => onToggleTax(i, e.target.checked)}
                      />
                      {li.taxUncertain && <span className="text-xs text-amber-700">confirm</span>}
                    </label>
                  </td>
                  {showMapping && (
                    <>
                      <td className="px-3 py-2">
                        {!workOrderResolved ? (
                          <span className="text-xs text-slate-400">
                            {previewLoading ? 'Checking…' : 'Look up the work order to map'}
                          </span>
                        ) : r == null ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : r.unmappedReason ? (
                          <Badge tone="red">{UNMAPPED_LABEL[r.unmappedReason] ?? r.unmappedReason}</Badge>
                        ) : (
                          <span className="text-slate-700">
                            {r.priceListItemName} · {formatMoney(r.rate, currency)}
                            {r.rateLocked && <span className="ml-1 text-xs text-slate-400">🔒</span>}
                            {r.viaFallback && (
                              <span
                                className="ml-2 inline-flex"
                                title="No mapping for this line — routed through the customer's Misc price-list item."
                              >
                                <Badge tone="amber">Misc fallback</Badge>
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r == null || r.unmappedReason
                          ? '—'
                          : formatMoney((r.quantity ?? 0) * (r.rate ?? 0), currency)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showMapping && anyDetail && (
        <p className="text-xs text-slate-400">
          Italicized detail under a parsed item is submitted as that Corrigo line’s{' '}
          <span className="font-medium text-slate-500">Description</span> (e.g. the materials list),
          while the line keeps its mapped price-list item name.
        </p>
      )}
    </div>
  );
}
